// Location Context - Provides location without causing re-renders
// Uses refs to prevent consumer re-renders when location changes
import { createContext, useContext, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface LocationContextType {
  getPathname: () => string;
  subscribe: (callback: (pathname: string) => void) => () => void;
}

const LocationContext = createContext<LocationContextType>({
  getPathname: () => '/',
  subscribe: () => () => {},
});

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  const subscribersRef = useRef<Set<(pathname: string) => void>>(new Set());
  
  useEffect(() => {
    if (pathnameRef.current !== location.pathname) {
      pathnameRef.current = location.pathname;
      // Notify subscribers without causing re-renders
      subscribersRef.current.forEach(callback => callback(location.pathname));
    }
  }, [location.pathname]);

  const contextValue = useRef<LocationContextType>({
    getPathname: () => pathnameRef.current,
    subscribe: (callback) => {
      subscribersRef.current.add(callback);
      return () => {
        subscribersRef.current.delete(callback);
      };
    },
  });

  return (
    <LocationContext.Provider value={contextValue.current}>
      {children}
    </LocationContext.Provider>
  );
};

// Hook that returns pathname without causing re-renders
// Components using this hook will NOT re-render when location changes
// Returns a function to get current pathname, not the pathname itself
export const useLocationPathname = () => {
  const context = useContext(LocationContext);
  return context.getPathname;
};

/** Full context for consumers that need to subscribe to pathname changes (e.g. Sidebar active state). */
export const useLocationContext = () => useContext(LocationContext);
